/**
 * Shared "this list scrolls" affordance for custom JS popovers / menus / pickers.
 *
 * macOS hides overlay scrollbars by default, so a scrollable popover
 * (`maxHeight` + `overflowY: auto`) looks like it has no more items below the
 * fold — which is exactly how Estimates' "Expired" status filter read as
 * missing (it was just below the cut, with no scrollbar to hint at it). Any
 * custom scrollable popover should opt in so overflow is always discoverable
 * on every platform, not just the ones that show scrollbars by default.
 *
 * `::-webkit-scrollbar` can't be set inline (it needs a stylesheet selector), so
 * the WebKit rules are injected once and target `[data-scroll-affordance]`;
 * Firefox is covered inline via `scrollAffordanceStyle`. Both only render a
 * scrollbar when the element actually overflows, so non-scrolling popovers are
 * visually unchanged.
 *
 * Usage in a component that renders a scrollable element:
 *   useEffect(() => { ensureScrollAffordanceStyles(); }, []);
 *   <ul {...SCROLL_AFFORDANCE_ATTR} style={{ ...listStyle, ...scrollAffordanceStyle }}>
 */

import { bg } from '../../ui/theme/semanticTokens';

const THUMB = bg.scrollThumb;
const THUMB_HOVER = bg.scrollThumbHover;

/** Spread onto the scrollable element so the injected WebKit rules match it. */
export const SCROLL_AFFORDANCE_ATTR = { 'data-scroll-affordance': '' };

/** Spread into the scrollable element's inline style (Firefox thin scrollbar). */
export const scrollAffordanceStyle = {
  scrollbarWidth: 'thin',
  scrollbarColor: `${THUMB} transparent`,
};

let injected = false;

/** Inject the WebKit scrollbar rules once. Idempotent; no-op during SSR/jsdom. */
export function ensureScrollAffordanceStyles() {
  if (injected || typeof document === 'undefined') return;
  injected = true;
  const style = document.createElement('style');
  style.setAttribute('data-scroll-affordance-styles', '');
  style.textContent = [
    '[data-scroll-affordance]::-webkit-scrollbar { width: 10px; height: 10px; }',
    `[data-scroll-affordance]::-webkit-scrollbar-thumb { background-color: ${THUMB}; border-radius: 8px; border: 3px solid transparent; background-clip: padding-box; }`,
    `[data-scroll-affordance]::-webkit-scrollbar-thumb:hover { background-color: ${THUMB_HOVER}; }`,
  ].join('\n');
  document.head.appendChild(style);
}
